'use strict';

const get = require('lodash/get');
const STORAGE_KEYS = require('public/js/lib/storage-keys');

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

const SEARCH_TYPE_PAGE = Object.freeze({
	MAIN_SEARCH: 'main_search',
});

/**
 * @param {string|null|undefined} query
 * @returns {string}
 */
function maskTerm(query) {
	const normalized = typeof query === 'string' ? query.trim() : '';
	if (normalized.length === 0) {
		return '';
	}

	return VIN_PATTERN.test(normalized.toUpperCase()) ? 'VIN search' : normalized;
}

/**
 * On-site keyword search results only (main_search).
 *
 * @returns {boolean}
 */
function isMainSearchPage() {
	return get(globalThis, 'tracking.search_type_page') === SEARCH_TYPE_PAGE.MAIN_SEARCH;
}

/**
 * PDP via ProductPageContext flag from PageView (not URL routes).
 *
 * @returns {boolean}
 */
function isPdp() {
	return get(globalThis, 'tracking.is_pdp') === true;
}

/**
 * @returns {string}
 */
function storageKey() {
	const hash = get(globalThis, 'tracking.dimensions.hash');
	if (typeof hash === 'string' && hash.length > 0) {
		return `${STORAGE_KEYS.ORIGINATING_SEARCH_TERM}:${hash}`;
	}

	return STORAGE_KEYS.ORIGINATING_SEARCH_TERM;
}

/**
 * @param {string|null|undefined} query
 * @returns {void}
 */
function persist(query) {
	const searchTerm = maskTerm(query);
	if (searchTerm.length === 0) {
		return;
	}

	try {
		sessionStorage.setItem(storageKey(), searchTerm);
	} catch (e) {
		// sessionStorage may be unavailable
	}
}

/**
 * @returns {string|null}
 */
function read() {
	try {
		const value = sessionStorage.getItem(storageKey());

		return typeof value === 'string' && value.length > 0 ? value : null;
	} catch (e) {
		return null;
	}
}

/**
 * @returns {void}
 */
function clear() {
	try {
		sessionStorage.removeItem(storageKey());
	} catch (e) {
		// sessionStorage may be unavailable
	}
}

/**
 * Clear immediately when outside main search → PDP (runs on every page_tracking).
 * DSEO / home / catalog / cart / other search pages clear here.
 *
 * @returns {void}
 */
function clearIfOffPath() {
	if (isPdp() || isMainSearchPage()) {
		return;
	}

	clear();
}

/**
 * Persist search term from dimensions on main search pages only.
 * Call before view_search_results clears the dimension.
 *
 * @returns {void}
 */
function persistFromPage() {
	if (!isMainSearchPage()) {
		return;
	}

	const searchTerm = get(globalThis, 'tracking.dimensions.search_term');
	if (typeof searchTerm !== 'string' || searchTerm.length === 0) {
		return;
	}

	persist(searchTerm);
}

/**
 * New tabs do not inherit sessionStorage. On PDP, adopt search_str from a
 * same-origin document.referrer query (no path/route checks).
 *
 * @returns {void}
 */
function adoptFromReferrerIfNeeded() {
	if (!isPdp() || read() !== null) {
		return;
	}

	try {
		const referrer = globalThis.document && globalThis.document.referrer;
		if (typeof referrer !== 'string' || referrer.length === 0) {
			return;
		}

		const refUrl = new URL(referrer);
		if (refUrl.origin !== globalThis.location.origin) {
			return;
		}

		const searchTerm = refUrl.searchParams.get('search_str');
		if (typeof searchTerm === 'string' && searchTerm.length > 0) {
			persist(searchTerm);
		}
	} catch (e) {
		// Invalid referrer URL or missing location
	}
}

/**
 * Term for ATC after off-path clear. PDP keeps a term that originated on search;
 * home / category / cart / DSEO / etc. clear first.
 *
 * @returns {string|null}
 */
function readOriginatingTerm() {
	clearIfOffPath();
	adoptFromReferrerIfNeeded();

	const term = read();
	if (term === null) {
		return null;
	}

	const masked = maskTerm(term);

	return masked.length > 0 ? masked : null;
}

module.exports = {
	SEARCH_TYPE_PAGE,
	VIN_PATTERN,
	adoptFromReferrerIfNeeded,
	clear,
	clearIfOffPath,
	isMainSearchPage,
	isPdp,
	maskTerm,
	persist,
	persistFromPage,
	read,
	readOriginatingTerm,
};
